/**
 * Authentication Security Utilities
 *
 * Provides JWT validation, idle timeout, and session monitoring
 */

import { jwtDecode } from 'jwt-decode';

// Configuration
const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
const ACTIVITY_EVENTS = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click'
];

interface JWTPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: any;
}

/**
 * Validate JWT token expiration
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;

  try {
    const decoded = jwtDecode<JWTPayload>(token);

    if (!decoded.exp) {
      return false; // If no exp, rely on backend validation
    }

    const currentTime = Date.now() / 1000;
    const isExpired = decoded.exp < currentTime;

    return isExpired;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return true; // If can't decode, consider expired
  }
};

/**
 * Get token expiration time in milliseconds
 */
export const getTokenExpirationTime = (token: string | null): number | null => {
  if (!token) return null;

  try {
    const decoded = jwtDecode<JWTPayload>(token);
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

/**
 * Get time until token expires (in milliseconds)
 */
export const getTimeUntilExpiration = (token: string | null): number | null => {
  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) return null;

  const timeRemaining = expirationTime - Date.now();
  return timeRemaining > 0 ? timeRemaining : 0;
};

/**
 * Idle Timeout Manager
 */
export class IdleTimeoutManager {
  private lastActivity: number;
  private timeoutId: NodeJS.Timeout | null = null;
  private checkIntervalId: NodeJS.Timeout | null = null;
  private onIdleCallback: () => void;
  private isActive: boolean = false;

  constructor(onIdle: () => void, timeout: number = IDLE_TIMEOUT) {
    this.lastActivity = Date.now();
    this.onIdleCallback = onIdle;
    this.timeoutId = null;
  }

  /**
   * Reset the idle timer
   */
  private resetTimer = (): void => {
    this.lastActivity = Date.now();
  };

  /**
   * Check if user has been idle
   */
  private checkIdle = (): void => {
    const idleTime = Date.now() - this.lastActivity;

    if (idleTime >= IDLE_TIMEOUT) {
      this.stop();
      this.onIdleCallback();
    }
  };

  /**
   * Start monitoring user activity
   */
  start(): void {
    if (this.isActive) return;

    this.isActive = true;
    this.lastActivity = Date.now();

    // Add event listeners for user activity
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, this.resetTimer, { passive: true });
    });

    // Check for idle every minute
    this.checkIntervalId = setInterval(this.checkIdle, 60 * 1000);
  }

  /**
   * Stop monitoring user activity
   */
  stop(): void {
    if (!this.isActive) return;

    this.isActive = false;

    // Remove event listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.removeEventListener(event, this.resetTimer);
    });

    // Clear interval
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  /**
   * Get time since last activity
   */
  getIdleTime(): number {
    return Date.now() - this.lastActivity;
  }

  /**
   * Check if currently idle
   */
  isIdle(): boolean {
    return this.getIdleTime() >= IDLE_TIMEOUT;
  }
}

/**
 * Session Monitor - Validates session periodically
 */
export class SessionMonitor {
  private intervalId: NodeJS.Timeout | null = null;
  private onSessionInvalidCallback: () => void;
  private isActive: boolean = false;

  constructor(onSessionInvalid: () => void) {
    this.onSessionInvalidCallback = onSessionInvalid;
  }

  /**
   * Validate current session
   */
  private validateSession = (): void => {
    try {
      // Check sessionStorage/localStorage for user and tokens
      const user =
        sessionStorage.getItem('user') || localStorage.getItem('user');
      const accessToken =
        sessionStorage.getItem('access_token') ||
        localStorage.getItem('access_token');

      if (!user || !accessToken) {
        this.stop();
        this.onSessionInvalidCallback();
        return;
      }

      // Validate token expiration
      if (isTokenExpired(accessToken)) {
        this.stop();
        this.onSessionInvalidCallback();
        return;
      }

      // Parse user data and check expiration
      const userData = JSON.parse(user);
      if (userData.expiresAt && Date.now() > userData.expiresAt) {
        this.stop();
        this.onSessionInvalidCallback();
        return;
      }

      // Session is valid
      const timeRemaining = getTimeUntilExpiration(accessToken);
    } catch (error) {
      console.error('Session validation error:', error);
      this.stop();
      this.onSessionInvalidCallback();
    }
  };

  /**
   * Start periodic session validation
   */
  start(): void {
    if (this.isActive) return;

    this.isActive = true;

    // Validate immediately
    this.validateSession();

    // Then validate every 5 minutes
    this.intervalId = setInterval(this.validateSession, SESSION_CHECK_INTERVAL);
  }

  /**
   * Stop session validation
   */
  stop(): void {
    if (!this.isActive) return;

    this.isActive = false;

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

/**
 * Consolidated storage manager
 */
export const authStorage = {
  /**
   * Set authentication data
   */
  set: (data: any, rememberMe: boolean = false): void => {
    const storage = rememberMe ? localStorage : sessionStorage;
    const key = 'auth';

    storage.setItem(key, JSON.stringify(data));

    // Also set tokens separately for axios interceptor
    if (data.tokens?.access_token) {
      storage.setItem('access_token', data.tokens.access_token);
    }
    if (data.tokens?.refresh_token) {
      storage.setItem('refresh_token', data.tokens.refresh_token);
    }

    // Store user data in old format for compatibility
    if (data.user) {
      const userData = {
        id: data.user.id,
        isLoggedIn: true,
        email: data.user.email,
        name: data.user.name,
        role: data.user.role_name,
        companyId: data.user.companyId,
        companies: [],
        loginTime: Date.now(),
        expiresAt: Date.now() + 60 * 60 * 1000,
        access_token: data.tokens?.access_token,
        refresh_token: data.tokens?.refresh_token,
        token_type: data.tokens?.token_type
      };
      storage.setItem('user', JSON.stringify(userData));
    }
  },

  /**
   * Get authentication data
   */
  get: (): any | null => {
    // Try auth key first
    const authData =
      localStorage.getItem('auth') || sessionStorage.getItem('auth');
    if (authData) {
      try {
        return JSON.parse(authData);
      } catch {
        // Ignore parse errors
      }
    }

    // Fallback to user key
    const userData =
      localStorage.getItem('user') || sessionStorage.getItem('user');
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch {
        // Ignore parse errors
      }
    }

    return null;
  },

  /**
   * Clear all authentication data
   */
  clear: (): void => {
    const keys = [
      'auth',
      'user',
      'access_token',
      'refresh_token',
      'remember_me'
    ];

    keys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  },

  /**
   * Get access token
   */
  getAccessToken: (): string | null => {
    return (
      localStorage.getItem('access_token') ||
      sessionStorage.getItem('access_token')
    );
  },

  /**
   * Get refresh token
   */
  getRefreshToken: (): string | null => {
    return (
      localStorage.getItem('refresh_token') ||
      sessionStorage.getItem('refresh_token')
    );
  }
};
